'use client';

import { QueryKeys } from '@/constants/query-keys';
import { theaterSearch } from '@/constants/theater-search';
import { theaterApiService } from '@/services/(frontend)/theaters/theater-api.service';
import { isValidLatLng } from '@/utils/location';
import { PagedListOfTheater } from '@cinemark/content-api-client';
import { useQuery } from '@tanstack/react-query';
import { useDynamicStaleTime } from '../shared/dynamic-stale-time';

export const INVALID_LAT_LNG = 200;

export const useTheatersByGeoLocation = (
  latitude: number,
  longitude: number,
  theaterIds?: number[],
  radius: number = theaterSearch.radius
): ReturnType<typeof useQuery<PagedListOfTheater>> => {
  const updateQueryStaleTime = useDynamicStaleTime();

  return useQuery<PagedListOfTheater>({
    enabled: isValidLatLng(latitude, longitude),
    queryFn: async () => {
      const response = await theaterApiService.getTheatersByGeo({
        latitude: latitude!,
        longitude: longitude!,
        pageSize: theaterSearch.limit,
        radius,
        theaterIds,
      });

      if (response.headers) {
        updateQueryStaleTime(QueryKeys.global.theatersByGeoLocation(latitude, longitude, theaterIds, radius), response.headers);
      }

      return response.data;
    },
    queryKey: QueryKeys.global.theatersByGeoLocation(latitude, longitude, theaterIds, radius),
  });
};

export const useTheatersByZipCode = (
  zipcode: string,
  theaterIds?: number[],
  radius: number = theaterSearch.radius
): ReturnType<typeof useQuery<PagedListOfTheater>> => {
  const updateQueryStaleTime = useDynamicStaleTime();

  return useQuery<PagedListOfTheater>({
    enabled: !!zipcode,
    queryFn: async () => {
      const response = await theaterApiService.getTheatersByZip({
        pageSize: theaterSearch.limit,
        radius,
        theaterIds,
        zipcode,
      });

      if (response.headers) {
        updateQueryStaleTime(QueryKeys.global.theatersByZipCode(zipcode, theaterIds, radius), response.headers);
      }

      return response.data;
    },
    queryKey: QueryKeys.global.theatersByZipCode(zipcode, theaterIds, radius),
  });
};

export const useTheatersByIds = (theaterIds: number[]): ReturnType<typeof useQuery<PagedListOfTheater>> => {
  const updateQueryStaleTime = useDynamicStaleTime();

  return useQuery<PagedListOfTheater>({
    enabled: !!theaterIds.length,
    queryFn: async () => {
      const response = await theaterApiService.getTheatersByTheaterIds({
        pageSize: theaterIds.length,
        theaterIds,
      });

      if (response.headers) {
        updateQueryStaleTime(QueryKeys.global.theatersByTheaterIds(theaterIds), response.headers);
      }

      return response.data;
    },
    queryKey: QueryKeys.global.theatersByTheaterIds(theaterIds),
  });
};
